import type { ResultOf } from '@graphql-typed-document-node/core';
import { stagedHouseholdSchema } from 'src/features/advisor-book-onboarding/utils/staged-book';
import { graphql } from 'src/gql';
import { z } from 'zod';

export const ADVISOR_BOOK_ONBOARDING_WELCOME_QUERY = graphql(/* GraphQL */ `
  query AdvisorBookOnboardingWelcome {
    currentUser {
      user {
        id
        firstName
        lastName
        fullName
        email
        phoneNumber
      }
    }
    advisorOpsContact {
      name
      firstName
      email
    }
    advisor {
      id
      captive
    }
  }
`);

export const CREATE_ADVISOR_ONBOARDING_ENROLLMENT_MUTATION = graphql(/* GraphQL */ `
  mutation CreateAdvisorOnboardingEnrollment {
    createAdvisorOnboardingEnrollment(input: {}) {
      enrollment {
        id
        uuid
        currentStep
        state
      }
    }
  }
`);

export const UPDATE_ADVISOR_ONBOARDING_ENROLLMENT_MUTATION = graphql(/* GraphQL */ `
  mutation UpdateAdvisorOnboardingEnrollment($input: UpdateEnrollmentInput!) {
    updateAdvisorOnboardingEnrollment(input: $input) {
      success
      errors {
        field
        message
      }
      enrollment {
        id
        uuid
        currentStep
        state
      }
    }
  }
`);

export const PROMOTE_ADVISOR_ONBOARDING_ENROLLMENT_MUTATION = graphql(/* GraphQL */ `
  mutation PromoteAdvisorOnboardingEnrollment($input: PromoteEnrollmentInput!) {
    promoteAdvisorOnboardingEnrollment(input: $input) {
      success
      promotedCount
      blockedCount
      skippedCount
      errors {
        field
        message
      }
      enrollment {
        id
        uuid
        currentStep
        state
        # Apollo normalizes these rows into the enrollment query cache for remount guards.
        stagedHouseholds {
          id
          householdId
          status
        }
      }
    }
  }
`);

export const EXCLUDE_ADVISOR_ONBOARDING_STAGED_HOUSEHOLDS_MUTATION = graphql(/* GraphQL */ `
  mutation ExcludeAdvisorOnboardingStagedHouseholdsFromBatch($input: ExcludeStagedHouseholdsFromBatchInput!) {
    excludeAdvisorOnboardingStagedHouseholdsFromBatch(input: $input) {
      success
      errors {
        field
        message
      }
      enrollment {
        id
        uuid
      }
    }
  }
`);

export const UPDATE_ADVISOR_ONBOARDING_STAGED_CLIENT_MUTATION = graphql(/* GraphQL */ `
  mutation UpdateAdvisorOnboardingStagedClient($input: UpdateStagedClientInput!) {
    updateAdvisorOnboardingStagedClient(input: $input) {
      stagedClient {
        id
        role
        firstName
        lastName
        email
        phone
        importErrors
      }
    }
  }
`);

export const UPDATE_ADVISOR_ONBOARDING_STAGED_HOUSEHOLD_MUTATION = graphql(/* GraphQL */ `
  mutation UpdateAdvisorOnboardingStagedHousehold($input: UpdateStagedHouseholdInput!) {
    updateAdvisorOnboardingStagedHousehold(input: $input) {
      stagedHousehold {
        id
        externalKey
        householdName
        estAum
        importErrors
        displayName
      }
    }
  }
`);

export const REMOVE_ADVISOR_ONBOARDING_STAGED_HOUSEHOLD_MUTATION = graphql(/* GraphQL */ `
  mutation RemoveAdvisorOnboardingStagedHousehold($input: RemoveStagedHouseholdInput!) {
    removeAdvisorOnboardingStagedHousehold(input: $input) {
      success
      deletedId
    }
  }
`);

export const REMOVE_ADVISOR_ONBOARDING_STAGED_CO_CLIENT_MUTATION = graphql(/* GraphQL */ `
  mutation RemoveAdvisorOnboardingStagedCoClient($input: RemoveStagedCoClientInput!) {
    removeAdvisorOnboardingStagedCoClient(input: $input) {
      success
      deletedId
    }
  }
`);

export const ADVISOR_ONBOARDING_ENROLLMENT_QUERY = graphql(/* GraphQL */ `
  query AdvisorOnboardingEnrollment($uuid: ID!) {
    advisorOnboardingEnrollment(uuid: $uuid) {
      id
      uuid
      currentStep
      state
      stagedHouseholds {
        id
        householdId
        status
        draft
        externalKey
        householdName
        estAum
        importErrors
        displayName
        stagedClients {
          id
          role
          firstName
          lastName
          email
          phone
          importErrors
        }
        stagedAccounts {
          id
          number
          accountName
          accountType
          custodian
          estimatedBalance
          importErrors
        }
      }
    }
  }
`);

export const CREATE_ADVISOR_BOOK_IMPORT_MUTATION = graphql(/* GraphQL */ `
  mutation CreateAdvisorBookImport($input: CreateBulkImportInput!) {
    createBulkImport(input: $input) {
      success
      errors
      bulkImport {
        id
        status
      }
    }
  }
`);

export const ADVISOR_BOOK_BULK_IMPORT_QUERY = graphql(/* GraphQL */ `
  query AdvisorBookBulkImport($id: ID!) {
    bulkImport(id: $id) {
      id
      status
      completionPercentage
      progress {
        total
        processed
        failed
        pending
      }
      errors {
        id
        message
        rowNumber
        sheetName
      }
    }
  }
`);

export const UPDATE_ADVISOR_ONBOARDING_USER_MUTATION = graphql(/* GraphQL */ `
  mutation UpdateAdvisorOnboardingUser($updateInput: UpdateUserInput!) {
    updateUser(input: $updateInput) {
      user {
        id
        firstName
        lastName
        fullName
        email
        phoneNumber
      }
    }
  }
`);

export const ADVISOR_ONBOARDING_PERSONALIZATION_QUERY = graphql(/* GraphQL */ `
  query AdvisorOnboardingPersonalization {
    advisorOnboardingPersonalization {
      id
      welcomeMessage
      firmName
      firmLogoUrl
    }
  }
`);

export const UPDATE_ONBOARDING_PERSONALIZATION_MUTATION = graphql(/* GraphQL */ `
  mutation UpdateOnboardingPersonalization($input: UpdateOnboardingPersonalizationInput!) {
    updateOnboardingPersonalization(input: $input) {
      success
      errors {
        field
        message
      }
      onboardingPersonalization {
        id
        welcomeMessage
        firmName
        firmLogoUrl
      }
    }
  }
`);

const advisorBookOnboardingWelcomeUserSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  fullName: z.string(),
  email: z.string(),
  phoneNumber: z.string().nullable(),
});

const advisorOpsContactSchema = z.object({
  name: z.string(),
  firstName: z.string(),
  email: z.string(),
});

const advisorOnboardingEnrollmentSchema = z
  .object({
    id: z.string(),
    uuid: z.string().uuid(),
    currentStep: z.string(),
    state: z.string(),
    stagedHouseholds: z.array(stagedHouseholdSchema).optional(),
  })
  .transform((enrollment) => ({
    ...enrollment,
    stagedHouseholds: enrollment.stagedHouseholds ?? [],
  }));

export type AdvisorBookOnboardingWelcomeUser = z.infer<typeof advisorBookOnboardingWelcomeUserSchema>;
export type AdvisorBookOnboardingOpsContact = z.infer<typeof advisorOpsContactSchema>;
export type AdvisorOnboardingEnrollment = z.infer<typeof advisorOnboardingEnrollmentSchema>;

const advisorOnboardingPersonalizationSchema = z.object({
  id: z.string(),
  welcomeMessage: z.string().nullable(),
  firmName: z.string().nullable(),
  firmLogoUrl: z.string().nullable(),
});

export type AdvisorOnboardingPersonalization = z.infer<typeof advisorOnboardingPersonalizationSchema>;

const bulkImportPollSchema = z.object({
  id: z.string(),
  status: z.string(),
  completionPercentage: z.number(),
  progress: z
    .object({
      total: z.number(),
      processed: z.number(),
      failed: z.number(),
      pending: z.number(),
    })
    .nullable(),
  errors: z.array(
    z.object({
      id: z.string(),
      message: z.string(),
      rowNumber: z.number().nullable(),
      sheetName: z.string().nullable(),
    }),
  ),
});

export type AdvisorBookBulkImportPoll = z.infer<typeof bulkImportPollSchema>;

export type AdvisorIntakeFormValues = {
  readonly name: string;
  readonly email: string;
  readonly phoneNumber: string;
};

const ADVISOR_ONBOARDING_ENROLLMENT_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isAdvisorOnboardingEnrollmentUuid(value: string | undefined): value is string {
  if (value === undefined || value === '') {
    return false;
  }

  return ADVISOR_ONBOARDING_ENROLLMENT_UUID_PATTERN.test(value);
}

type AdvisorBookOnboardingWelcomeQueryData = ResultOf<typeof ADVISOR_BOOK_ONBOARDING_WELCOME_QUERY>;

// errorPolicy: 'all' can null schema-non-null fields (e.g. advisor) when that field errors.
type AdvisorBookOnboardingWelcomeQueryDataWithPartialAdvisor = Omit<
  AdvisorBookOnboardingWelcomeQueryData,
  'advisor'
> & {
  readonly advisor: AdvisorBookOnboardingWelcomeQueryData['advisor'] | null;
};

export function getAdvisorBookOnboardingWelcomeUser(
  data: AdvisorBookOnboardingWelcomeQueryData | undefined,
): AdvisorBookOnboardingWelcomeUser | undefined {
  if (data === undefined) {
    return undefined;
  }

  const user = data.currentUser.user;
  if (user === null) {
    return undefined;
  }

  const parsed = advisorBookOnboardingWelcomeUserSchema.safeParse(user);
  return parsed.success ? parsed.data : undefined;
}

export function getAdvisorBookOnboardingOpsContact(
  data: AdvisorBookOnboardingWelcomeQueryData | undefined,
): AdvisorBookOnboardingOpsContact | null | undefined {
  if (data === undefined) {
    return undefined;
  }

  const contact = data.advisorOpsContact;
  if (contact === null) {
    return null;
  }

  const parsed = advisorOpsContactSchema.safeParse(contact);
  return parsed.success ? parsed.data : undefined;
}

export function getAdvisorBookOnboardingCaptive(
  data: AdvisorBookOnboardingWelcomeQueryDataWithPartialAdvisor | undefined,
) {
  if (data === undefined) {
    return undefined;
  }

  if (data.advisor === null) {
    return undefined;
  }

  return data.advisor.captive;
}

export function getCreatedAdvisorOnboardingEnrollment(
  data: ResultOf<typeof CREATE_ADVISOR_ONBOARDING_ENROLLMENT_MUTATION> | null | undefined,
): AdvisorOnboardingEnrollment | undefined {
  if (data === null || data === undefined) {
    return undefined;
  }

  const payload = data.createAdvisorOnboardingEnrollment;
  if (payload === null) {
    return undefined;
  }

  const enrollment = payload.enrollment;
  const parsed = advisorOnboardingEnrollmentSchema.safeParse(enrollment);
  return parsed.success ? parsed.data : undefined;
}

export function getUpdatedAdvisorOnboardingEnrollment(
  data: ResultOf<typeof UPDATE_ADVISOR_ONBOARDING_ENROLLMENT_MUTATION> | null | undefined,
): AdvisorOnboardingEnrollment | undefined {
  if (data === null || data === undefined) {
    return undefined;
  }

  const payload = data.updateAdvisorOnboardingEnrollment;
  if (payload === null || !payload.success || payload.enrollment === null) {
    return undefined;
  }

  const parsed = advisorOnboardingEnrollmentSchema.safeParse(payload.enrollment);
  return parsed.success ? parsed.data : undefined;
}

const promoteEnrollmentResultSchema = z.object({
  success: z.boolean(),
  promotedCount: z.number(),
  blockedCount: z.number(),
  skippedCount: z.number(),
  errors: z
    .array(
      z.object({
        field: z.string().nullable(),
        message: z.string(),
      }),
    )
    .nullable(),
  enrollment: z
    .object({
      id: z.string(),
      uuid: z.string().uuid(),
      currentStep: z.string(),
      state: z.string(),
    })
    .nullable(),
});

export type PromoteAdvisorOnboardingEnrollmentResult = z.infer<typeof promoteEnrollmentResultSchema>;

export function getPromoteAdvisorOnboardingEnrollmentResult(
  data: ResultOf<typeof PROMOTE_ADVISOR_ONBOARDING_ENROLLMENT_MUTATION> | null | undefined,
): PromoteAdvisorOnboardingEnrollmentResult | undefined {
  if (data === null || data === undefined) {
    return undefined;
  }

  const payload = data.promoteAdvisorOnboardingEnrollment;
  if (payload === null) {
    return undefined;
  }

  const parsed = promoteEnrollmentResultSchema.safeParse(payload);
  return parsed.success ? parsed.data : undefined;
}

export function excludeAdvisorOnboardingStagedHouseholdsSucceeded(
  data: ResultOf<typeof EXCLUDE_ADVISOR_ONBOARDING_STAGED_HOUSEHOLDS_MUTATION> | null | undefined,
): boolean {
  if (data === null || data === undefined) {
    return false;
  }

  const payload = data.excludeAdvisorOnboardingStagedHouseholdsFromBatch;
  return payload !== null && payload.success;
}

export function updateStagedClientSucceeded(
  data: ResultOf<typeof UPDATE_ADVISOR_ONBOARDING_STAGED_CLIENT_MUTATION> | null | undefined,
): boolean {
  if (data === null || data === undefined) {
    return false;
  }

  const payload = data.updateAdvisorOnboardingStagedClient;
  return payload !== null;
}

export function updateStagedHouseholdSucceeded(
  data: ResultOf<typeof UPDATE_ADVISOR_ONBOARDING_STAGED_HOUSEHOLD_MUTATION> | null | undefined,
): boolean {
  if (data === null || data === undefined) {
    return false;
  }

  const payload = data.updateAdvisorOnboardingStagedHousehold;
  return payload !== null;
}

export function removeStagedHouseholdSucceeded(
  data: ResultOf<typeof REMOVE_ADVISOR_ONBOARDING_STAGED_HOUSEHOLD_MUTATION> | null | undefined,
): boolean {
  if (data === null || data === undefined) {
    return false;
  }

  const payload = data.removeAdvisorOnboardingStagedHousehold;
  return payload !== null && payload.success;
}

export function removeStagedCoClientSucceeded(
  data: ResultOf<typeof REMOVE_ADVISOR_ONBOARDING_STAGED_CO_CLIENT_MUTATION> | null | undefined,
): boolean {
  if (data === null || data === undefined) {
    return false;
  }

  const payload = data.removeAdvisorOnboardingStagedCoClient;
  return payload !== null && payload.success;
}

export function getAdvisorOnboardingEnrollment(
  data: ResultOf<typeof ADVISOR_ONBOARDING_ENROLLMENT_QUERY> | undefined,
): AdvisorOnboardingEnrollment | null | undefined {
  if (data === undefined) {
    return undefined;
  }

  const enrollment = data.advisorOnboardingEnrollment;
  if (enrollment === null) {
    return null;
  }

  const parsed = advisorOnboardingEnrollmentSchema.safeParse(enrollment);
  return parsed.success ? parsed.data : undefined;
}

export function getAdvisorBookBulkImportPoll(
  data: ResultOf<typeof ADVISOR_BOOK_BULK_IMPORT_QUERY> | undefined,
): AdvisorBookBulkImportPoll | null | undefined {
  if (data === undefined) {
    return undefined;
  }

  const bulkImport = data.bulkImport;
  if (bulkImport === null) {
    return null;
  }

  const parsed = bulkImportPollSchema.safeParse(bulkImport);
  return parsed.success ? parsed.data : undefined;
}

export function getCreatedAdvisorBookBulkImportId(
  data: ResultOf<typeof CREATE_ADVISOR_BOOK_IMPORT_MUTATION> | null | undefined,
): { readonly bulkImportId: string | undefined; readonly errors: readonly string[]; readonly success: boolean } {
  if (data === null || data === undefined) {
    return { bulkImportId: undefined, errors: [], success: false };
  }

  const payload = data.createBulkImport;
  if (payload === null) {
    return { bulkImportId: undefined, errors: [], success: false };
  }

  return {
    bulkImportId: payload.bulkImport?.id,
    errors: payload.errors,
    success: payload.success,
  };
}

export function getAdvisorOnboardingPersonalization(
  data: ResultOf<typeof ADVISOR_ONBOARDING_PERSONALIZATION_QUERY> | undefined,
): AdvisorOnboardingPersonalization | null | undefined {
  if (data === undefined) {
    return undefined;
  }

  const personalization = data.advisorOnboardingPersonalization;
  if (personalization === null) {
    return null;
  }

  const parsed = advisorOnboardingPersonalizationSchema.safeParse(personalization);
  return parsed.success ? parsed.data : undefined;
}

export function getUpdatedOnboardingPersonalization(
  data: ResultOf<typeof UPDATE_ONBOARDING_PERSONALIZATION_MUTATION> | null | undefined,
): AdvisorOnboardingPersonalization | undefined {
  if (data === null || data === undefined) {
    return undefined;
  }

  const payload = data.updateOnboardingPersonalization;
  if (payload === null || !payload.success || payload.onboardingPersonalization === null) {
    return undefined;
  }

  const parsed = advisorOnboardingPersonalizationSchema.safeParse(payload.onboardingPersonalization);
  return parsed.success ? parsed.data : undefined;
}

export function getOnboardingPersonalizationMutationErrors(
  data: ResultOf<typeof UPDATE_ONBOARDING_PERSONALIZATION_MUTATION> | null | undefined,
): readonly { readonly field: string | null; readonly message: string }[] {
  if (data === null || data === undefined) {
    return [];
  }

  const payload = data.updateOnboardingPersonalization;
  if (payload === null || payload.errors === null) {
    return [];
  }

  return payload.errors;
}

type OpsContactNameParts = {
  readonly name: string;
  readonly firstName: string;
};

export function getOpsContactLastName({ name, firstName }: OpsContactNameParts): string {
  const trimmedName = name.trim();
  const trimmedFirstName = firstName.trim();

  if (trimmedName.startsWith(`${trimmedFirstName} `) || trimmedName === trimmedFirstName) {
    return trimmedName.slice(trimmedFirstName.length).trim();
  }

  const nameParts = trimmedName.split(/\s+/);
  return nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
}

type SplitFullNameResult = {
  readonly firstName: string;
  readonly lastName: string;
};

export function splitAdvisorIntakeFullName(fullName: string): SplitFullNameResult {
  const trimmed = fullName.trim();
  const parts = trimmed.split(/\s+/).filter((part) => part !== '');

  if (parts.length === 0) {
    return { firstName: '', lastName: '' };
  }

  return {
    firstName: parts[0] ?? '',
    lastName: parts.slice(1).join(' '),
  };
}

type SplitStagedClientFullNameParams = {
  readonly fullName: string;
  readonly originalFirstName: string | null;
  readonly originalLastName?: string | null;
};

/** Preserves a multi-word first name when a real last name already existed. */
export function splitStagedClientFullName({
  fullName,
  originalFirstName,
  originalLastName,
}: SplitStagedClientFullNameParams): SplitFullNameResult {
  const trimmedName = fullName.trim();
  const trimmedOriginalFirstName = (originalFirstName ?? '').trim();
  const trimmedOriginalLastName = (originalLastName ?? '').trim();

  if (
    trimmedOriginalFirstName !== '' &&
    trimmedOriginalLastName !== '' &&
    (trimmedName === trimmedOriginalFirstName || trimmedName.startsWith(`${trimmedOriginalFirstName} `))
  ) {
    return {
      firstName: trimmedOriginalFirstName,
      lastName: trimmedName.slice(trimmedOriginalFirstName.length).trim(),
    };
  }

  return splitAdvisorIntakeFullName(trimmedName);
}

type SplitAdvisorIntakeFullNameForUpdateParams = {
  readonly fullName: string;
  readonly originalUser: AdvisorBookOnboardingWelcomeUser;
};

export function splitAdvisorIntakeFullNameForUpdate({
  fullName,
  originalUser,
}: SplitAdvisorIntakeFullNameForUpdateParams): SplitFullNameResult {
  const trimmedName = fullName.trim();
  const trimmedOriginalFirstName = originalUser.firstName.trim();

  if (
    trimmedOriginalFirstName !== '' &&
    (trimmedName === trimmedOriginalFirstName || trimmedName.startsWith(`${trimmedOriginalFirstName} `))
  ) {
    return {
      firstName: trimmedOriginalFirstName,
      lastName: trimmedName.slice(trimmedOriginalFirstName.length).trim(),
    };
  }

  return splitAdvisorIntakeFullName(trimmedName);
}

type BuildAdvisorIntakeUpdateInputParams = {
  readonly formValues: AdvisorIntakeFormValues;
  readonly originalUser: AdvisorBookOnboardingWelcomeUser;
  readonly formattedOriginalPhone: string;
  readonly normalizePhone: (displayPhone: string) => string;
};

export function buildAdvisorIntakeUpdateInput({
  formValues,
  originalUser,
  formattedOriginalPhone,
  normalizePhone,
}: BuildAdvisorIntakeUpdateInputParams) {
  const updateInput: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
  } = {};

  const trimmedName = formValues.name.trim();
  if (trimmedName !== originalUser.fullName.trim()) {
    const { firstName, lastName } = splitAdvisorIntakeFullNameForUpdate({
      fullName: trimmedName,
      originalUser,
    });
    updateInput.firstName = firstName;
    updateInput.lastName = lastName;
  }

  const trimmedEmail = formValues.email.trim();
  if (trimmedEmail !== originalUser.email) {
    updateInput.email = trimmedEmail;
  }

  const trimmedPhone = formValues.phoneNumber.trim();
  if (trimmedPhone !== formattedOriginalPhone) {
    updateInput.phoneNumber = normalizePhone(trimmedPhone);
  }

  return updateInput;
}
